import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from './layouts/AppLayout'
import { AuthLayout } from './layouts/AuthLayout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { useAuthStore } from './store/authStore'
import LandingPage from './pages/landing/LandingPage'

function HomeRedirect() {
  const user = useAuthStore((s) => s.user)
  const roles = user?.roles ?? []
  if (roles.includes('viewer')) return <Navigate to="/viewer" replace />
  // Wave 1 E1.2 — dispatchers land on the unified dispatch console.
  if (roles.includes('dispatcher') && !roles.includes('admin') && !roles.includes('user'))
    return <Navigate to="/dispatch" replace />
  if (roles.includes('driver')) return <Navigate to="/driver" replace />
  return <Navigate to="/dashboard" replace />
}

import LoginPage from './pages/auth/LoginPage'
import SecuritySettingsPage from './pages/settings/SecuritySettingsPage'
import NotificationPreferencesPage from './pages/settings/NotificationPreferencesPage'
import ShareTrackingPage from './pages/share/ShareTrackingPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import UsersPage from './pages/users/UsersPage'
import UserDetailPage from './pages/users/UserDetailPage'
import RolesPage from './pages/users/RolesPage'
import DevicesPage from './pages/devices/DevicesPage'
import DeviceDetailPage from './pages/devices/DeviceDetailPage'
import DeviceGroupsPage from './pages/devices/DeviceGroupsPage'
import AudienceGroupsPage from './pages/audiences/AudienceGroupsPage'
import DeviceAssignmentsPage from './pages/devices/DeviceAssignmentsPage'
import SensorDefinitionsPage from './pages/devices/SensorDefinitionsPage'
import DigitalTwinPage from './pages/digital-twin/DigitalTwinPage'
import RulesPage from './pages/rules/RulesPage'
import AlertsPage from './pages/rules/AlertsPage'
import AnalyticsPage from './pages/analytics/AnalyticsPage'
import NotificationsPage from './pages/notifications/NotificationsPage'
import SendNotificationPage from './pages/notifications/SendNotificationPage'
import TrustCirclePage from './pages/trust-circle/TrustCirclePage'
import BillingPage from './pages/billing/BillingPage'
import DriverDashboardPage from './pages/driver/DriverDashboardPage'
import DispatchConsolePage from './pages/dispatch/DispatchConsolePage'
import TripsPage from './pages/trips/TripsPage'
import ViewerDashboardPage from './pages/viewer/ViewerDashboardPage'
import MapPage from './pages/map/MapPage'
import TripPlaybackPage from './pages/map/TripPlaybackPage'
import GeofencesPage from './pages/fleet/GeofencesPage'
import JobsPage from './pages/fleet/JobsPage'
import MaintenancePage from './pages/fleet/MaintenancePage'
import CommandsPage from './pages/fleet/CommandsPage'
import ReportsPage from './pages/reports/ReportsPage'
import FuelAnalyticsPage from './pages/fleet/FuelAnalyticsPage'
import TripComparisonPage from './pages/trips/TripComparisonPage'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import PrivacyPolicyPageAr from './pages/legal/PrivacyPolicyPageAr'
import SupportPage from './pages/legal/SupportPage'
import CustomersPage from './pages/customers/CustomersPage'
import InvoicingPage from './pages/customers/InvoicingPage'
import ColdChainPage from './pages/customers/ColdChainPage'
import JobTrackingPage from './pages/share/JobTrackingPage'

export const router = createBrowserRouter([
  // ── Auth ──────────────────────────────────────────────────────
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { index: true, element: <Navigate to="login" replace /> },
    ],
  },

  // ── Tenant app (admin / user / driver / viewer) ───────────────
  {
    path: '/',
    element: (
      <ProtectedRoute fallback={<LandingPage />}>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: 'dashboard',          element: <DashboardPage /> },
      { path: 'users',              element: <UsersPage /> },
      { path: 'users/:id',          element: <UserDetailPage /> },
      { path: 'roles',              element: <RolesPage /> },
      { path: 'devices',            element: <DevicesPage /> },
      { path: 'devices/:id',        element: <DeviceDetailPage /> },
      { path: 'device-groups',      element: <DeviceGroupsPage /> },
      { path: 'audiences',          element: <AudienceGroupsPage /> },
      { path: 'device-assignments', element: <DeviceAssignmentsPage /> },
      { path: 'sensor-definitions', element: <SensorDefinitionsPage /> },
      { path: 'digital-twin',       element: <DigitalTwinPage /> },
      { path: 'rules',              element: <RulesPage /> },
      { path: 'alerts',             element: <AlertsPage /> },
      { path: 'analytics',          element: <AnalyticsPage /> },
      { path: 'notifications',      element: <NotificationsPage /> },
      { path: 'send-notification',  element: <SendNotificationPage /> },
      { path: 'trust-circle',       element: <TrustCirclePage /> },
      { path: 'billing',            element: <BillingPage /> },
      { path: 'driver',             element: <DriverDashboardPage /> },
      { path: 'dispatch',           element: <DispatchConsolePage /> },
      { path: 'trips',              element: <TripsPage /> },
      { path: 'viewer',             element: <ViewerDashboardPage /> },
      { path: 'map',                element: <MapPage /> },
      { path: 'playback',           element: <TripPlaybackPage /> },
      { path: 'geofences',          element: <GeofencesPage /> },
      { path: 'jobs',               element: <JobsPage /> },
      { path: 'maintenance',        element: <MaintenancePage /> },
      { path: 'commands',           element: <CommandsPage /> },
      { path: 'reports',            element: <ReportsPage /> },
      { path: 'fuel-analytics',    element: <FuelAnalyticsPage /> },
      { path: 'trip-comparison',   element: <TripComparisonPage /> },
      { path: 'security',           element: <SecuritySettingsPage /> },
      { path: 'notification-preferences', element: <NotificationPreferencesPage /> },
      // ── Wave 2 ───────────────────────────────────────────────
      { path: 'customers',          element: <CustomersPage /> },
      { path: 'invoicing',          element: <InvoicingPage /> },
      { path: 'cold-chain',         element: <ColdChainPage /> },
    ],
  },

  // ── Public pages (no auth) ──────────────────────────────────
  { path: '/track/:token', element: <ShareTrackingPage /> },
  { path: '/track/job/:token', element: <JobTrackingPage /> },
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  { path: '/ar/privacy', element: <PrivacyPolicyPageAr /> },
  { path: '/support', element: <SupportPage /> },

  { path: '*', element: <Navigate to="/" replace /> },
])
